import { type MouseEvent, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import { packageSelectionCartButtonStyles } from 'components/AccommodationKit/AccommodationPackageSelection/packageSelectionMobileFooterStyles'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { getFlashPackageQuoteContext } from 'lib/offer/flashPurchasePolicy'
import { getFlashPackageSelectionCapabilities } from './useFlashPackageSelectionCapabilities'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import CurrencyContext from 'contexts/currencyContext'
import { OfferPageBurnSelectionContext } from 'contexts/OfferPage/OfferPageBurnSelectionContext'
import FlashPackageSelectionActions, { type FlashPackageSelectionActionsProps } from './FlashPackageSelectionActions'
import FlashPackageSelectionOptionsSheet from './FlashPackageSelectionOptionsSheet'
import PurchaseContextChangedMessage from 'components/OfferPage/PurchaseContextChangedMessage'
import type { FlashSameRoomSelection, OfferPageMultiRoomMode } from 'contexts/OfferPage/offerPageStateReducer'

interface Props {
  packageView?: App.OfferPackageOptionView
  sameRoomSelection?: FlashSameRoomSelection
  roomMode?: OfferPageMultiRoomMode
  onInvalidSelection?: () => void
  rooms: Array<App.RoomOccupants>
  baseBnblPackageViews: Array<App.OfferPackageOptionView>
  canRedeemLuxPlusBenefits: boolean
  charterBundleRoute?: App.CharterBundleRoute
  offer: App.HotelOffer
  checkIn?: string
  checkOut?: string
  duration?: number
  presentation?: 'icon'
  /** Icon presentation size. Small matches the 32px mobile footer Book now button. */
  size?: 'small' | 'medium'
  disabled?: boolean
}

const OptionsIconButton = styled(IconButton)`
  &.size-small {
    ${packageSelectionCartButtonStyles}
  }
`

export function FlashPackageSelectionOptions({ packageView, sameRoomSelection, roomMode, onInvalidSelection = noop, rooms, baseBnblPackageViews, canRedeemLuxPlusBenefits, charterBundleRoute, offer, checkIn, checkOut, duration, presentation, size = 'medium', disabled = false }: Props) {
  const intl = useIntl()
  const currency = useContext(CurrencyContext)
  const { quote: selectionBurnQuote } = useContext(OfferPageBurnSelectionContext)
  const purchaseReady = !disabled && (!selectionBurnQuote || selectionBurnQuote.ready)
  const latestPurchaseReady = useRef(purchaseReady)
  latestPurchaseReady.current = purchaseReady
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')
  const id = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [contextChanged, setContextChanged] = useState(false)
  const modalOpen = useRef(false)
  const bnblSessionOpen = useRef(false)
  const intentMemberId = useRef(memberId)
  const modalResolve = useRef<(() => void) | undefined>(undefined)
  const closeActions = useCallback(() => {
    modalOpen.current = false
    setOpen(false)
    modalResolve.current?.()
  }, [])
  const showInvalidSelection = useCallback(() => {
    closeActions()
    onInvalidSelection()
  }, [closeActions, onInvalidSelection])
  useEffect(() => () => {
    modalOpen.current = false
    modalResolve.current?.()
  }, [])
  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(packageView?.package)
  const { canAddToCart, canBuyNowBookLater } = useMemo(() => getFlashPackageSelectionCapabilities({ offer, packageView, roomCount: rooms.length, checkIn, canRedeemLuxPlusBenefits, baseBnblPackageViews, charterBundleRoute, upgradeApplied: !!selectedTargetUpgradePackage }),
    [baseBnblPackageViews, canRedeemLuxPlusBenefits, charterBundleRoute, checkIn, offer, packageView, rooms.length, selectedTargetUpgradePackage])
  const actions = useMemo<FlashPackageSelectionActionsProps>(() => ({
    canAddToCart,
    canBuyNowBookLater,
    packageView,
    requireCommittedSameRoomSelection: rooms.length > 1,
    onInvalidSelection: showInvalidSelection,
    onSubmit: closeActions,
  }), [canAddToCart, canBuyNowBookLater, closeActions, packageView, rooms.length, showInvalidSelection])
  // BNBL has its own editable selection. A change to the page that originated the flow still
  // invalidates that draft: do not submit an outdated offer, guests, currency or quote.
  const contextKey = useMemo(() => JSON.stringify([
    offer.id, offer.hasBuyNowBookLater, offer.bookByDate, offer.travelToDate, offer.luxPlus,
    checkIn, checkOut, duration, currency, rooms, canRedeemLuxPlusBenefits, charterBundleRoute,
    roomMode, sameRoomSelection, getFlashPackageQuoteContext(packageView),
    baseBnblPackageViews.map(getFlashPackageQuoteContext), canBuyNowBookLater, !!selectedTargetUpgradePackage,
  ]), [offer.id, offer.hasBuyNowBookLater, offer.bookByDate, offer.travelToDate, offer.luxPlus, checkIn, checkOut, duration, currency, rooms, canRedeemLuxPlusBenefits, charterBundleRoute, packageView, roomMode, sameRoomSelection, baseBnblPackageViews, canBuyNowBookLater, selectedTargetUpgradePackage])
  const intentContext = useRef(contextKey)
  const latestMemberId = useRef(memberId)
  const latestContext = useRef(contextKey)
  const closeBnblModalRef = useRef<(() => void) | undefined>(undefined)
  latestMemberId.current = memberId
  latestContext.current = contextKey
  const onRegisterBnblClose = useCallback((close: (() => void) | undefined) => {
    closeBnblModalRef.current = close
  }, [])
  const captureIntent = useCallback(() => {
    intentMemberId.current = latestMemberId.current
    intentContext.current = latestContext.current
  }, [])
  const onBnblSessionChange = useCallback((open: boolean) => {
    const wasOpen = bnblSessionOpen.current
    bnblSessionOpen.current = open
    if (open && !wasOpen) captureIntent()
  }, [captureIntent])
  useEffect(() => {
    const flowOpen = modalOpen.current || bnblSessionOpen.current
    // First login establishes ownership. Once known, a later account or logout invalidates the flow.
    if (!intentMemberId.current) {
      if (flowOpen && memberId) intentMemberId.current = memberId
      return
    }
    if (intentMemberId.current === memberId) return
    if (flowOpen) {
      setContextChanged(true)
      closeBnblModalRef.current?.()
    }
    closeActions()
  }, [closeActions, memberId])
  useEffect(() => {
    if (intentContext.current === contextKey) return
    if (modalOpen.current || bnblSessionOpen.current) {
      setContextChanged(true)
      closeBnblModalRef.current?.()
    }
    closeActions()
  }, [contextKey, closeActions])
  const beforePurchase = useCallback(() => {
    const flowOpen = modalOpen.current || bnblSessionOpen.current
    const sameAccount = !intentMemberId.current || intentMemberId.current === latestMemberId.current
    const sameContext = intentContext.current === latestContext.current
    if (flowOpen && sameContext && sameAccount && canBuyNowBookLater && latestPurchaseReady.current) return true
    closeBnblModalRef.current?.()
    setContextChanged(true)
    closeActions()
    return false
  }, [canBuyNowBookLater, closeActions])
  const close = useCallback(() => setOpen(false), [])
  const onDesktopActionClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const target = event.target
    // Modal portals share the React tree, but their actions are outside this menu.
    if (!(target instanceof Element) || !event.currentTarget.contains(target) || !target.closest('button, a')) return
    close()
  }, [close])
  const onOpen = useCallback(async() => {
    if (!latestPurchaseReady.current) return
    captureIntent()
    if (isMobile) {
      if (modalOpen.current) return
      setContextChanged(false)
      modalOpen.current = true
      setOpen(true)
      try {
        await showModal<void>((resolve) => {
          modalResolve.current = resolve
          return <FlashPackageSelectionOptionsSheet offer={offer} actions={actions} onClose={closeActions} onBeforePurchase={beforePurchase} />
        })
      } finally {
        modalOpen.current = false
        modalResolve.current = undefined
        setOpen(false)
        triggerRef.current?.focus()
      }
    } else {
      setOpen(value => !value)
    }
  }, [actions, beforePurchase, captureIntent, closeActions, isMobile, offer, showModal])
  const moreOptionsLabel = intl.formatMessage({ id: 'opm_gcN43uO6BEd282LB', defaultMessage: 'More options', description: 'Button label for alternate Flash hotel package purchase actions' })

  const triggerProps = {
    ref: triggerRef,
    kind: 'secondary' as const,
    onClick: onOpen,
    'aria-label': moreOptionsLabel,
    'aria-haspopup': isMobile ? 'dialog' as const : 'true' as const,
    'aria-expanded': open,
    disabled: !purchaseReady,
    'aria-controls': open && !isMobile ? id : undefined,
  }

  return <>
    {contextChanged && <PurchaseContextChangedMessage />}
    {presentation === 'icon' && <OptionsIconButton {...triggerProps} shape="circle" size={size}>
      <LineEllipsisVIcon />
    </OptionsIconButton>}
    {presentation !== 'icon' && <TextButton
      {...triggerProps}
      size="medium"
      shape="round"
      fit="full-width"
      startIcon={<LineEllipsisVIcon />}
    >
      {moreOptionsLabel}
    </TextButton>}
    {!isMobile && <DropdownList
      id={id}
      size="S"
      width="auto"
      placement="bottom-start"
      crossAxisShift
      open={open}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      onClose={close}
      portaled
      keepOpenOnModalClick
    >
      <div onClickCapture={onDesktopActionClick}>
        <FlashPackageSelectionActions
          {...actions}
          onBeforePurchase={beforePurchase}
          purchaseSessionKey={`${memberId ?? ''}:${contextKey}`}
          onPurchaseSessionChange={onBnblSessionChange}
          onRegisterClose={onRegisterBnblClose}
        />
      </div>
    </DropdownList>}
  </>
}

export default withOfferPageAndReduxState((state, appState): Pick<Props, 'offer' | 'checkIn' | 'checkOut' | 'duration' | 'rooms' | 'baseBnblPackageViews' | 'canRedeemLuxPlusBenefits' | 'charterBundleRoute' | 'sameRoomSelection' | 'roomMode'> => ({
  offer: state.offer as App.HotelOffer,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  rooms: state.rooms,
  sameRoomSelection: state.flashSameRoomSelection,
  roomMode: state.multiRoomMode,
  baseBnblPackageViews: getBnblPackageViews(state),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  charterBundleRoute: state.charterBundleRoute,
}), FlashPackageSelectionOptions)
